import { ref, reactive, computed, watch } from "vue";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { CasinoJackpotAllInfo, CasinoJackpotType } from "./casino-jackpot";
import { useNgCasinoState } from "@sazka/casino/js/core/use-ng-casino-state";

const refreshInterval = 20000; // in ms
const jackpots = reactive<CasinoJackpotAllInfo>({
    jackpotsByProvider: {
        [CasinoJackpotType.Apollo]: [],
        [CasinoJackpotType.Kajot]: [],
        [CasinoJackpotType.Synot]: [],
        [CasinoJackpotType.Tech4Bet]: [],
        [CasinoJackpotType.Adell]: [],
        [CasinoJackpotType.Amusnet]: [],
        [CasinoJackpotType.EGaming]: [],
        [CasinoJackpotType.Casimi]: [],
        [CasinoJackpotType.Isoftbet]: [],
        [CasinoJackpotType.Thunderkick]: [],
        [CasinoJackpotType.Endorphina]: [],
        [CasinoJackpotType.TomHorn]: [],
    },
    totalJackpot: null,
});
const subscribedProviders = ref<(CasinoJackpotType | "total")[]>([]);
const subscribedProvidersSet = computed(() => new Set(subscribedProviders.value));
let hasInitialized = false;
let isUpdating = false;
let intervalId: NodeJS.Timeout | number | null = null;

const getNewJackpots = async (getAllJackpots = false): Promise<CasinoJackpotAllInfo> => {
    const apiUrl = "/api/casino/jackpot";

    if (!getAllJackpots && !subscribedProvidersSet.value.size) return Promise.resolve(null);

    try {
        const getTotal = subscribedProvidersSet.value.has("total");

        const providersArray = getAllJackpots
            ? Object.values(CasinoJackpotType)
            : [...subscribedProviders.value].filter((provider) => provider !== "total");

        const providersQuery =
            subscribedProvidersSet.value.size > 0 || getAllJackpots
                ? "providers=" + providersArray.map((provider) => provider.toLowerCase()).join(",") + "&"
                : "";

        const response = await httpClient.get<CasinoJackpotAllInfo>(
            `${apiUrl}?${providersQuery}total=${getTotal || getAllJackpots}`
        );

        return response.data;
    } catch (error) {
        console.error("api error", error);
        return Promise.resolve(null);
    }
};

const updateJackpots = async (loadAllJackpots = false) => {
    const jackpotData = await getNewJackpots(loadAllJackpots);

    if (!jackpotData) return;

    for (const provider in jackpotData.jackpotsByProvider) {
        jackpots.jackpotsByProvider[provider] = jackpotData.jackpotsByProvider[provider];
    }

    jackpots.totalJackpot = jackpotData.totalJackpot;
};

const startUpdating = async (loadAllJackpots = false) => {
    if (isUpdating) return;
    isUpdating = true;
    await updateJackpots(loadAllJackpots);
    intervalId = setInterval(() => updateJackpots(), refreshInterval);
};
const stopUpdating = () => {
    if (!isUpdating) return;
    isUpdating = false;
    clearInterval(intervalId);
};

const watchGameHandler = async (newGameId: number) => {
    if (newGameId !== 0) {
        stopUpdating();
    }

    if (newGameId === 0) {
        await startUpdating(true);
    }
};

const initializeJackpotManager = async () => {
    hasInitialized = true;
    await startUpdating(true);

    const { currentGameId } = useNgCasinoState();

    watch(currentGameId, (newGameId) => watchGameHandler(newGameId));
};

const useJackpotManager = async (jackpotProviders: (CasinoJackpotType | "total")[]) => {
    let hasSubscribed = false;

    const subscribe = async () => {
        if (!hasSubscribed && jackpotProviders.length) {
            hasSubscribed = true;

            jackpotProviders = jackpotProviders.map(
                (provider) => provider.toLowerCase() as CasinoJackpotType | "total"
            );
            subscribedProviders.value.push(...jackpotProviders);

            if (!hasInitialized) {
                await initializeJackpotManager();
            }
        }
    };

    const unsubscribe = () => {
        if (!hasSubscribed) return;

        for (const provider of jackpotProviders) {
            const index = subscribedProviders.value.indexOf(provider);
            if (index > -1) {
                subscribedProviders.value.splice(index, 1);
            }
        }

        hasSubscribed = false;
    };

    await subscribe();

    return {
        jackpots,
        subscribe,
        unsubscribe,
    };
};

export default useJackpotManager;
